import React from 'react';
import { fmt, fmtDate } from '../lib/utils';
import OccupancyPanel from './OccupancyPanel';

// Strip seconds from any stored "HH:MM:SS" time — we only display HH:MM.
const fmtTime = (t) => (typeof t === 'string' && t.length > 5 ? t.slice(0, 5) : (t || ''));

// ─────────────────────────────────────────────────────────────────────────────
// FunctionSheetRenderer — single source of truth for the single-event
// function-sheet display (EventCard on the Function Sheets screen, the
// Event View print, and the per-event "Print Single Event" modal).
//
// Layout (2026-04-23, "Option C"):
//   1. Hero header      — big event name, couple/client line, date · time ·
//                         venue · guests metadata strip.
//   2. Services block   — emphasised ("the focus") — larger text, tinted
//                         background, prominent time column. This is what
//                         kitchen and floor staff reference on the day.
//   3. Operational table — 2-col table (label · value) for setup, menu,
//                         dietary, equipment, financials. Only rows with a
//                         value render, so sheets stay dense.
//   4. Notes callout    — amber-tinted block at the bottom for any loose
//                         one-off info.
//
// NOTE: the Daily and Weekly print views do NOT use this component — they
// render their own condensed multi-event table directly in FunctionSheets.jsx
// (that's the view that genuinely needs to cram several events onto one A4
// page). This component only ever prints ONE event per page, so as of
// 2026-08-28 its print styles no longer shrink text/spacing down to the
// 9-10px used there — on a single-event sheet that just made the sheet look
// cramped and unprofessional compared to the on-screen version, for no
// space-saving benefit. Print now renders close to the on-screen sizes;
// print: overrides are only used to flatten tinted screen panels to
// black-bordered boxes and darken borders for legibility on paper.
// ─────────────────────────────────────────────────────────────────────────────

// Service / F&B preset labels — keep in sync with Events.jsx FB_PRESETS
export const FB_PRESET_LABELS = {
  morning_tea: 'Morning Tea',
  afternoon_tea: 'Afternoon Tea',
  tea_coffee: 'Tea & Coffee',
  lunch: 'Lunch',
  dinner: 'Dinner',
  dessert: 'Dessert',
  welcome_cocktail: 'Welcome Cocktail',
  cocktail_hour: 'Cocktail Hour',
  canapes: 'Canapés',
  wine_selection: 'Wine Selection',
  cheese_board: 'Cheese Board',
  sandwiches: 'Sandwiches',
  cake: 'Cake',
  breakfast: 'Breakfast',
  evening_snacks: 'Evening Snacks',
  welcome_drinks: 'Welcome Drinks',
  evening_reception: 'Evening Reception',
  after_party_room: 'After Party Room',
  bbq: 'BBQ',
};

export function getServiceLabel(item) {
  if (item == null) return '';
  if (typeof item === 'string') {
    return FB_PRESET_LABELS[item] || item.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  const name = item.name || item.item || '';
  const label = FB_PRESET_LABELS[name] || (name ? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');
  const time = fmtTime(item.time || item.service_time || '');
  const qty = item.quantity;
  return [label, time && `@ ${time}`, qty && `× ${qty}`].filter(Boolean).join(' ');
}

// Merge structured services (from Events / RoomDiary forms) + preset fbItems
// into a single list. Each entry: { time, description, location }
export function collectServiceEntries(event) {
  const out = [];
  if (event && Array.isArray(event.services)) {
    event.services.forEach((s) => {
      if (!s) return;
      const desc = (s.description || '').trim();
      const time = fmtTime((s.time || '').trim());
      const loc = (s.location || '').trim();
      if (desc || time || loc) out.push({ time, description: desc, location: loc });
    });
  }
  if (event && Array.isArray(event.fbItems)) {
    event.fbItems.forEach((item) => {
      const label = getServiceLabel(item);
      if (label) out.push({ time: '', description: label, location: '' });
    });
  }
  return out.sort((a, b) => {
    if (a.time && !b.time) return -1;
    if (!a.time && b.time) return 1;
    return (a.time || '').localeCompare(b.time || '');
  });
}

/**
 * ServicesSection
 *
 * Props:
 *   emphasized (bool, default true) — bigger text, tinted background, bold
 *     time column. This is the default because services are the focus of
 *     a function sheet. Pass `emphasized={false}` for a quieter inline list.
 */
export function ServicesSection({
  event,
  label = 'Services',
  className,
  emphasized = true,
}) {
  const entries = collectServiceEntries(event);
  if (entries.length === 0) return null;

  if (emphasized) {
    // Screen: roomy emerald-tinted panel so services pop visually.
    // Print: same size/spacing as screen, just flattened to a black-bordered
    // box (tinted backgrounds print unevenly, so ink-only borders instead).
    const cls = className ?? 'mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 print:bg-transparent print:border print:border-slate-400 print:rounded-none';
    return (
      <div className={cls}>
        <p className="text-[11px] font-bold text-emerald-800 uppercase tracking-[0.08em] mb-2 print:text-slate-900">
          {label}
        </p>
        <ul className="space-y-1.5">
          {entries.map((s, i) => (
            <li key={i} className="flex gap-3 items-baseline print:gap-2">
              <span className="font-bold text-emerald-900 text-base min-w-[56px] tabular-nums print:text-slate-900">
                {s.time || '—'}
              </span>
              <span className="flex-1 text-slate-900 text-[15px] leading-snug">
                {s.description}
                {s.location ? <span className="text-slate-600 text-sm"> · {s.location}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Quiet inline version (used in some summary contexts).
  const cls = className ?? 'mb-3';
  return (
    <div className={cls}>
      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{label}</p>
      <ul className="text-sm text-gray-800 space-y-0.5">
        {entries.map((s, i) => (
          <li key={i} className="flex gap-3 items-baseline">
            <span className="font-semibold text-gray-900 min-w-[56px] tabular-nums">{s.time || '—'}</span>
            <span className="flex-1">
              {s.description}
              {s.location ? <span className="text-gray-500"> · {s.location}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FunctionSheetBody — hero + services + operational table + notes.
// Used in: Room Diary modal, Function Sheets page (single event + per-event
// block in Daily/Weekly views), and Events page's function-sheet tab.
// ─────────────────────────────────────────────────────────────────────────────
export function FunctionSheetBody({
  event,
  venues = [],
  // Controls whether financial rows (package total, room hire, price per
  // head, billing type) appear in the operational table. Defaults to true;
  // set false if you want a kitchen-only sheet with no money on it.
  showFinancials = true,
  // Hide the hero header when the caller has already rendered a custom
  // header (e.g. the Room Diary modal wraps it in its own heading bar).
  showHero = true,
  // `compact` tightens the spacing — used for dense multi-event daily
  // prints where every mm counts.
  compact = false,
  // Hide the in-house panel when the caller shows it once for the whole day
  // (Function Sheets daily / weekly lists) instead of on every event card.
  showOccupancy = true,
}) {
  if (!event) return null;
  const venue = venues.find((v) => v.id === event.venue_id);
  const venueName = venue?.name || event.venue_id || '';

  const clientLine = (event.brideName || event.groomName)
    ? [event.brideName, event.groomName].filter(Boolean).join(' & ')
    : (event.client || event.clientName || '');

  const dateText = event.date ? fmtDate(event.date) : '';
  const timeText = [event.startTime, event.endTime].filter(Boolean).map(fmtTime).join(' – ');
  const metaItems = [
    dateText,
    timeText,
    venueName,
    event.guests != null && event.guests !== '' ? `${event.guests} guests` : null,
  ].filter(Boolean);

  // Build the operational-detail table. Each entry shows only when the
  // value is actually populated — so a sparsely-filled event still reads
  // tight instead of being a column of dashes.
  const equipmentText = Array.isArray(event.equipment) && event.equipment.length > 0
    ? event.equipment.map((eq) => (typeof eq === 'string' ? eq : (eq?.name || ''))).filter(Boolean).join(', ')
    : '';
  const dietaryText = Array.isArray(event.dietaryRequirements)
    ? event.dietaryRequirements.join(', ')
    : (event.dietaryRequirements || '');

  const rawRows = [
    { label: 'Contact', value: [event.clientPhone, event.clientEmail].filter(Boolean).join(' · ') },
    { label: 'Event type', value: event.type ? event.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '' },
    { label: 'Room setup', value: event.setup ? event.setup.charAt(0).toUpperCase() + event.setup.slice(1) : '' },
    { label: 'Setup requirements', value: event.setupRequirements },
    { label: 'Menu', value: event.menuNotes },
    { label: 'Dietary', value: dietaryText },
    { label: 'Equipment', value: equipmentText },
  ];
  if (showFinancials) {
    rawRows.push(
      { label: 'Package total', value: event.packageTotal ? fmt(event.packageTotal) : '' },
      { label: 'Room hire', value: event.roomHire ? fmt(event.roomHire) : '' },
      { label: 'Price per head', value: event.pricePerHead ? fmt(event.pricePerHead) : '' },
      { label: 'Billing', value: event.billingType ? event.billingType.replace(/_/g, ' ') : '' },
    );
  }
  const rows = rawRows.filter((r) => r.value !== '' && r.value !== null && r.value !== undefined);

  const padY = compact ? 'py-1.5' : 'py-2';

  // NOTE: the page-break-inside rule is applied by the CALLER on the
  // outermost per-event wrapper (via the `.event-block` class defined in
  // App.css). Putting an inline page-break-inside here as well caused Chrome
  // to ignore both rules due to nested avoid regions — removed.
  return (
    <div className="text-slate-900">
      {showHero && (
        <div className="border-b-2 border-slate-900 pb-3 mb-4 print:border-b">
          <h3 className="text-xl md:text-2xl font-bold leading-tight">
            {event.name || '(Untitled event)'}
          </h3>
          {clientLine && (
            <p className="text-sm text-slate-600 mt-1">{clientLine}</p>
          )}
          {metaItems.length > 0 && (
            <p className="text-sm text-slate-800 mt-1.5 font-medium">
              {metaItems.join('  ·  ')}
            </p>
          )}
        </div>
      )}

      {/* Hotel in-house figures for the event night(s) — near the top so
          kitchen / floor staff see it at a glance. Tap to enter or edit. */}
      {showOccupancy && event.date && (
        <div className="mb-4">
          <OccupancyPanel date={event.date} endDate={event.endDate} />
        </div>
      )}

      {/* Services — the focus. Emphasised by default. */}
      <ServicesSection event={event} emphasized />

      {/* Operational detail table */}
      {rows.length > 0 && (
        <table className="w-full text-sm mb-4 border-collapse">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-200 last:border-b-0 print:border-slate-400">
                <th
                  scope="row"
                  className={`text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide pr-3 w-40 align-top ${padY}`}
                >
                  {r.label}
                </th>
                <td className={`text-slate-900 whitespace-pre-wrap ${padY}`}>
                  {r.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}


      {/* Notes callout — visually distinct, since it's often the "pay
          attention to this" field (cake delivery times, early arrivals). */}
      {event.notes && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 print:bg-transparent print:border print:border-slate-400 print:rounded-none">
          <p className="text-[11px] font-bold text-amber-900 uppercase tracking-[0.08em] mb-1 print:text-slate-900">
            Notes
          </p>
          <p className="text-sm text-slate-900 whitespace-pre-wrap">{event.notes}</p>
        </div>
      )}
    </div>
  );
}

export default FunctionSheetBody;
